import { useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { ArrowUp, ArrowUpRight, Dribbble, Github, Linkedin, Twitter } from "lucide-react";
import { LogoFull } from "@/components/Logo";
import { useTransition } from "@/context/UIContext";
import { BRAND, NAV, SERVICES, SOCIALS } from "@/data/content";

const SOCIAL_ICONS = { GitHub: Github, Twitter: Twitter, LinkedIn: Linkedin, Dribbble: Dribbble };

export default function Footer() {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.15 });
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end end"] });
    const x = useTransform(scrollYProgress, [0, 1], ["4%", "-6%"]);
    const { go } = useTransition();
    const toTop = () => {
        if (window.__lenis) window.__lenis.scrollTo(0);
        else window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const colClass = "flex flex-col gap-3";
    const linkClass = "group flex w-fit items-center gap-1.5 text-sm text-frost transition-colors duration-300 hover:text-electric";

    return (
        <footer ref={ref} className="relative overflow-hidden border-t border-white/5 bg-navy-950" data-testid="site-footer">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(0,128,255,0.12),transparent_60%)]" />

            {/* Giant animated word */}
            <div className="relative select-none overflow-hidden pt-14" aria-hidden>
                <motion.div style={{ x }} className="whitespace-nowrap font-display text-[17vw] font-bold leading-none tracking-tight text-stroke-faint">
                    ZENITHIX
                </motion.div>
                <motion.div style={{ x }} className="absolute inset-x-0 top-14 whitespace-nowrap font-display text-[17vw] font-bold leading-none tracking-tight" animate={{ opacity: inView ? 1 : 0 }} transition={{ duration: 1 }}>
                    <span className="animate-gradient-pan bg-gradient-to-r from-transparent via-electric to-transparent bg-clip-text text-transparent">
                        ZENITHIX
                    </span>
                </motion.div>
            </div>

            <div className="relative mx-auto grid max-w-7xl gap-12 px-6 py-16 md:grid-cols-[1.4fr_1fr_1fr_1.2fr] md:gap-8">
                {[
                    <div key="brand" className={colClass}>
                        <LogoFull />
                        <p className="max-w-xs text-sm leading-relaxed text-frost">
                            Where innovation meets growth. We turn bold ideas into powerful digital solutions that scale.
                        </p>
                        <div className="mt-2 flex gap-3">
                            {SOCIALS.map((s, i) => {
                                const Icon = SOCIAL_ICONS[s.name];
                                return (
                                    <motion.a
                                        key={s.name}
                                        href={s.href}
                                        target="_blank"
                                        rel="noreferrer"
                                        aria-label={s.name}
                                        data-testid={`social-${s.name.toLowerCase()}`}
                                        initial={{ opacity: 0, scale: 0 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true }}
                                        whileHover={{ y: -3 }}
                                        transition={{ delay: 0.2 + i * 0.07, type: "spring", stiffness: 260, damping: 18 }}
                                        className="grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-navy-800/60 text-frost transition-colors duration-300 hover:border-electric hover:text-electric hover:shadow-glow"
                                    >
                                        <Icon className="h-4 w-4" />
                                    </motion.a>
                                );
                            })}
                        </div>
                    </div>,
                    <div key="company" className={colClass}>
                        <div className="font-mono text-[10px] tracking-[0.3em] text-electric">COMPANY</div>
                        {NAV.map((item) => (
                            <button key={item.path} onClick={() => go(item.path, item.transition)} data-testid={`footer-link-${item.label.toLowerCase()}`} className={linkClass}>
                                {item.label}
                                <ArrowUpRight className="h-3 w-3 opacity-0 transition-all duration-300 group-hover:translate-x-0.5 group-hover:opacity-100" />
                            </button>
                        ))}
                    </div>,
                    <div key="services" className={colClass}>
                        <div className="font-mono text-[10px] tracking-[0.3em] text-electric">SERVICES</div>
                        {SERVICES.map((s) => (
                            <button key={s.id} onClick={() => go("/services", "OUR SERVICES")} data-testid={`footer-service-${s.id}`} className={linkClass}>
                                {s.name}
                                <ArrowUpRight className="h-3 w-3 opacity-0 transition-all duration-300 group-hover:translate-x-0.5 group-hover:opacity-100" />
                            </button>
                        ))}
                    </div>,
                    <div key="contact" className={colClass}>
                        <div className="font-mono text-[10px] tracking-[0.3em] text-electric">GET IN TOUCH</div>
                        <a href={`mailto:${BRAND.email}`} className={linkClass} data-testid="footer-email">{BRAND.email}</a>
                        {BRAND.phones.map((phone, index) => <a key={phone} href={`tel:${phone.replace(/[^+\d]/g, "")}`} className={linkClass} data-testid={index === 0 ? "footer-phone" : `footer-phone-${index + 1}`}>{phone}</a>)}
                        <p className="text-sm text-frost">{BRAND.address}</p>
                        <button onClick={toTop} data-testid="back-to-top" className={`${linkClass} mt-2 font-medium text-ice`}>
                            <ArrowUp className="h-4 w-4" /> Back to top
                        </button>
                    </div>,
                ].map((col, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 28 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ delay: i * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    >
                        {col}
                    </motion.div>
                ))}
            </div>

            <div className="relative border-t border-white/5">
                <div className="relative h-px w-full overflow-visible">
                    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-electric/40 to-transparent" />
                    <div className="absolute top-0 h-px w-28 animate-sheen bg-gradient-to-r from-transparent via-electric to-transparent" style={{ filter: "drop-shadow(0 0 6px rgba(0,128,255,0.9))" }} />
                </div>
                <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-6 py-6 text-xs text-frost sm:flex-row">
                    <span>© 2025 Zenithix. All rights reserved.</span>
                    <motion.span animate={inView ? { opacity: [0.4, 1, 0.4] } : {}} transition={{ duration: 3, repeat: Infinity }} className="font-mono tracking-widest text-electric/80">
                        IDEAS → SOLUTIONS → GROWTH
                    </motion.span>
                </div>
            </div>
        </footer>
    );
}
